import React, { useEffect, useState, useCallback } from 'react';
import { api } from '../api.js';
import { useLang } from '../i18n.js';
import { thb, thbShort, shortDate } from '../money.js';
import {
  Segmented,
  Empty,
  Spinner,
  Avatar,
  EntityGlyph,
  Tier,
  entityColor,
  Overlay,
} from '../ui.jsx';

const STAGE_COLORS = [
  'var(--ink-3)',
  'var(--info)',
  'var(--btm-purple-2)',
  'var(--warning)',
  'var(--success)',
  'var(--danger)',
];

/**
 * New Customer Deals / Upsell Deals.
 *
 * Same component for both boards — they differ by `kind`, which changes the
 * stage set and who owns the cards. Board is the default; the table is there
 * for the case the design calls out explicitly: an account with fifteen
 * near-identical monthly retainer rows, which a kanban renders as noise.
 */
export default function Deals({ kind, config, me, onOpenDeal, toast }) {
  const { t, lang } = useLang();
  const entities = me.entities.length ? me.entities : ['BTM', 'MR'];

  const [entity, setEntity] = useState(entities[0]);
  const [view, setView] = useState('board');
  const [mine, setMine] = useState(false);
  const [data, setData] = useState(null);
  const [rows, setRows] = useState(null);
  const [error, setError] = useState('');
  const [dragOver, setDragOver] = useState(null);
  const [dragging, setDragging] = useState(null);
  const [losing, setLosing] = useState(null);

  const load = useCallback(() => {
    setError('');
    const params = { entity, kind, ...(mine ? { owner_id: me.id } : {}) };
    if (view === 'board') {
      api.board(params).then(setData).catch((e) => setError(e.message));
    } else {
      api.deals({ ...params, open: '0' }).then(setRows).catch((e) => setError(e.message));
    }
  }, [entity, kind, mine, view, me.id]);

  useEffect(() => {
    load();
  }, [load]);

  const stages = kind === 'upsell' ? config.stages_upsell : config[`stages_${entity.toLowerCase()}`];

  /** Optimistic move: the card lands in the new column immediately and only
   *  reconciles if the server disagrees. Watching a spinner to drag a card is
   *  exactly the friction that made v2 feel slow. */
  async function move(dealId, toStage, extra) {
    if (/^lost$/i.test(toStage) && !extra) {
      setLosing({ id: dealId, stage: toStage });
      return;
    }
    const snapshot = data;
    setData((d) => {
      if (!d) return d;
      let moved = null;
      const columns = d.columns.map((col) => {
        const keep = col.deals.filter((x) => {
          if (x.id === dealId) {
            moved = x;
            return false;
          }
          return true;
        });
        return { ...col, deals: keep };
      });
      if (!moved) return d;
      return {
        ...d,
        columns: columns.map((col) =>
          col.stage === toStage ? { ...col, deals: [{ ...moved, stage: toStage }, ...col.deals] } : col,
        ),
      };
    });

    try {
      await api.moveStage(dealId, { stage: toStage, ...(extra || {}) });
      toast(t('moved', { s: toStage }));
      load();
    } catch (e) {
      setData(snapshot);
      toast(e.message);
    }
  }

  return (
    <>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          flexWrap: 'wrap',
          padding: '18px 24px 8px 24px',
        }}
      >
        <div className="display" style={{ fontSize: 22, marginRight: 'auto' }}>
          {kind === 'upsell' ? t('navUpsells') : t('navDeals')}
        </div>

        {entities.length > 1 && (
          <Segmented
            value={entity}
            onChange={setEntity}
            options={entities.map((e) => ({ value: e, label: e }))}
          />
        )}
        <Segmented
          value={view}
          onChange={setView}
          options={[
            { value: 'board', label: t('board') },
            { value: 'table', label: t('table') },
          ]}
        />
        <button className={`chip${mine ? ' is-on' : ''}`} onClick={() => setMine((v) => !v)}>
          {mine ? me.name.split(' ')[0] : t('allOwners')}
        </button>
      </div>

      {error && <div className="page">{error}</div>}

      {!error && view === 'board' &&
        (!data ? (
          <Spinner label={t('loading')} />
        ) : (
          <>
            <div
              style={{
                padding: '0 24px 6px 24px',
                fontSize: 12.5,
                color: 'var(--ink-2)',
              }}
            >
              <strong className="num">{thb(data.total_satang)}</strong> · {data.total_count}{' '}
              {t('openPipeline').toLowerCase()}
            </div>

            <div className="board">
              {data.columns.map((col, i) => (
                <div
                  key={col.stage}
                  className={`col${dragOver === col.stage ? ' is-over' : ''}`}
                  onDragOver={(e) => {
                    e.preventDefault();
                    setDragOver(col.stage);
                  }}
                  onDragLeave={() => setDragOver((s) => (s === col.stage ? null : s))}
                  onDrop={(e) => {
                    e.preventDefault();
                    setDragOver(null);
                    const id = e.dataTransfer.getData('text/plain');
                    if (id && col.stage !== dragging?.stage) move(id, col.stage);
                    setDragging(null);
                  }}
                >
                  <div className="col-head">
                    <span
                      style={{
                        fontSize: 12.5,
                        fontWeight: 700,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                      }}
                    >
                      <span
                        style={{
                          width: 7,
                          height: 7,
                          borderRadius: '50%',
                          background: STAGE_COLORS[i % STAGE_COLORS.length],
                        }}
                      />
                      {col.stage}
                      <span style={{ color: 'var(--ink-3)', fontWeight: 600 }}>· {col.count}</span>
                    </span>
                    <span className="num" style={{ fontSize: 11.5, color: 'var(--ink-2)' }}>
                      {thbShort(col.total_satang)}
                    </span>
                  </div>

                  <div
                    style={{ display: 'flex', flexDirection: 'column', gap: 8, minHeight: 60 }}
                  >
                    {col.deals.length === 0 && (
                      <div style={{ fontSize: 12, color: 'var(--ink-3)', padding: '10px 6px' }}>
                        {t('noDeals')}
                      </div>
                    )}
                    {col.deals.map((d) => (
                      <div
                        key={d.id}
                        className={`deal-card${dragging?.id === d.id ? ' is-dragging' : ''}`}
                        draggable
                        onDragStart={(e) => {
                          e.dataTransfer.setData('text/plain', d.id);
                          e.dataTransfer.effectAllowed = 'move';
                          setDragging({ id: d.id, stage: col.stage });
                        }}
                        onDragEnd={() => {
                          setDragging(null);
                          setDragOver(null);
                        }}
                        onClick={() => onOpenDeal(d.id)}
                      >
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            marginBottom: 4,
                          }}
                        >
                          <span
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 5,
                              fontWeight: 700,
                              fontSize: 10.5,
                              color: entityColor(d.entity_tag),
                            }}
                          >
                            <EntityGlyph entity={d.entity_tag} />
                            {d.code}
                          </span>
                          <Tier tier={d.tier || d.company_tier} />
                        </div>
                        <div style={{ fontSize: 13.5, fontWeight: 600, lineHeight: 1.35 }}>
                          {d.title}
                        </div>
                        <div
                          style={{ fontSize: 12, color: 'var(--ink-2)', margin: '2px 0 8px 0' }}
                        >
                          {d.company_name}
                        </div>
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                          }}
                        >
                          <span className="num" style={{ fontSize: 13, fontWeight: 700 }}>
                            {thb(d.amount_satang)}
                          </span>
                          <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            {d.idle_warn && (
                              <span
                                style={{ fontSize: 10.5, color: 'var(--warning)', fontWeight: 700 }}
                              >
                                {t('idle', { n: d.idle_days })}
                              </span>
                            )}
                            <Avatar
                              initials={d.owner_initials}
                              seed={d.owner_var}
                              size={21}
                              title={d.owner_name}
                            />
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {data.unknown_stages.length > 0 && (
              <div className="page" style={{ paddingTop: 0, fontSize: 12.5, color: 'var(--warning)' }}>
                Deals sit in stages that are no longer configured:{' '}
                {data.unknown_stages.join(', ')}. They are hidden from the board.
              </div>
            )}
          </>
        ))}

      {!error && view === 'table' &&
        (!rows ? (
          <Spinner label={t('loading')} />
        ) : (
          <div style={{ padding: '4px 24px 24px 24px' }}>
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>{t('deal')}</th>
                    <th>{t('titleClient')}</th>
                    <th>{t('stage')}</th>
                    <th>{t('salesRep')}</th>
                    <th style={{ textAlign: 'right' }}>{t('amount')}</th>
                    <th>{t('close')}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.deals.map((d) => (
                    <tr key={d.id} onClick={() => onOpenDeal(d.id)}>
                      <td>
                        <span
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 6,
                            fontWeight: 700,
                            fontSize: 11,
                            color: entityColor(d.entity_tag),
                          }}
                        >
                          <EntityGlyph entity={d.entity_tag} />
                          {d.code}
                        </span>
                      </td>
                      <td style={{ whiteSpace: 'normal', minWidth: 260 }}>
                        <div style={{ fontWeight: 600 }}>{d.title}</div>
                        <div style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>
                          {d.company_name}
                        </div>
                      </td>
                      <td>
                        <span className="pill">{d.stage}</span>
                      </td>
                      <td>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                          <Avatar initials={d.owner_initials} seed={d.owner_var} size={20} />
                          {d.owner_name || '—'}
                        </span>
                      </td>
                      <td className="num" style={{ textAlign: 'right', fontWeight: 700 }}>
                        {thb(d.amount_satang)}
                      </td>
                      <td>{shortDate(d.close_date, lang)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {rows.deals.length === 0 && <Empty>{t('noDeals')}</Empty>}
            <div style={{ marginTop: 10, fontSize: 12.5, color: 'var(--ink-2)' }}>
              {rows.total} deals · <strong className="num">{thb(rows.total_satang)}</strong>
            </div>
          </div>
        ))}

      {losing && (
        <LossModal
          config={config}
          onCancel={() => setLosing(null)}
          onConfirm={(extra) => {
            const { id, stage } = losing;
            setLosing(null);
            move(id, stage, extra);
          }}
        />
      )}
    </>
  );
}

/** A loss with no reason is a loss nobody learns from — so the server refuses
 *  it, and this is where the user is asked. */
function LossModal({ config, onCancel, onConfirm }) {
  const { t } = useLang();
  const [reason, setReason] = useState(config.loss_reasons?.[0] || '');
  const [competitor, setCompetitor] = useState('');
  const [notes, setNotes] = useState('');

  return (
    <Overlay onClose={onCancel} kind="modal">
      <div style={{ padding: '18px 20px 8px 20px' }}>
        <div className="display" style={{ fontSize: 18 }}>
          {t('lossReason')}
        </div>
      </div>
      <div style={{ padding: '0 20px', overflow: 'auto' }}>
        <label className="field-label">{t('lossReason')}</label>
        <select className="select" value={reason} onChange={(e) => setReason(e.target.value)}>
          {(config.loss_reasons || []).map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </select>
        <label className="field-label" style={{ marginTop: 12 }}>
          {t('competitor')}
        </label>
        <input
          className="input"
          value={competitor}
          onChange={(e) => setCompetitor(e.target.value)}
        />
        <label className="field-label" style={{ marginTop: 12 }}>
          {t('details')}
        </label>
        <textarea
          className="textarea"
          rows={3}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
      </div>
      <div className="drawer-foot" style={{ marginTop: 12 }}>
        <button className="btn" onClick={onCancel} style={{ marginLeft: 'auto' }}>
          {t('cancel')}
        </button>
        <button
          className="btn btn-primary"
          disabled={!reason}
          onClick={() =>
            onConfirm({ loss_reason: reason, loss_competitor: competitor, loss_notes: notes })
          }
        >
          {t('markLost')}
        </button>
      </div>
    </Overlay>
  );
}
